import type { Invoice } from "@prisma/client";
import { PaymentMode } from "@prisma/client";
import { jsPDF } from "jspdf";

import dayjs from "@calcom/dayjs";

type InvoiceData = Invoice & {
  account: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    foreignId: string | null;
    patientId: number;
  };
  products: Array<{
    name: string;
    description: string | null;
    quantity: number;
    price: number;
  }>;
  office: {
    name: string;
    officename: string;
    logo: string | null;
    headerColor: string | null;
    timeZone: string;
  };
};

const labels = {
  invoiceName: "Invoice",
  invoiceNumber: "Number",
  fromLabel: "Who is this from?",
  billTo: "Bill To",
  shipTo: "Ship To",
  date: "Date",
  paymentTerms: "Payment Terms",
  dueDate: "Due Date",
  poNumber: "PO Number",
  productService: "Product/Service",
  description: "Description",
  quantity: "Quantity",
  unitPrice: "Unit Price",
  amount: "Amount",
  notes: "Notes",
  terms: "Terms",
  subtotal: "Subtotal",
  tax: "Tax",
  discount: "Discount",
  shipping: "Shipping",
  total: "Total",
  amountPaid: "Amount Paid",
  amountDue: "Amount Due",
};

export default async function generateInvoicePdf(invoiceData: InvoiceData) {
  const {
    account,
    office,
    products,
    date,
    dueDate,
    dueAmount,
    paymentTerms,
    purchaseOrderNumber,
    includePaymentLink,
    notes,
    terms,
    attachLink,
    tax,
    discount,
    shipping,
    paidAmount,
    taxMode,
    discountMode,
  } = invoiceData;

  let y = 10;
  const maxWidth = 440;
  const minWidth = 10;

  const normalFontSize = 12;
  const largeFontSize = 16;
  const xLargeFontSize = 20;

  const font = "helvetica";

  const black = "#000";
  const gray = "#666";
  const bgGray = "#ddd";
  const white = "#fff";

  const doc = new jsPDF({
    unit: "px",
    format: "a4",
  });

  doc.setLineWidth(5);
  doc.setDrawColor(office.headerColor || "#000");
  doc.line(0, 0, 450, 0);

  doc.setFont(font, "normal");

  // render logo
  if (office.logo) {
    // Create a temporary image element to get the original dimensions
    const img = new Image();
    img.src = office.logo;

    await new Promise((resolve) => {
      img.onload = () => {
        const maxWidth = 120;
        const maxHeight = 50;

        // Calculate aspect ratio
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const width = img.width * ratio;
        const height = img.height * ratio;

        y += height;

        doc.addImage(office.logo || "", "PNG", minWidth, 10, width, height);
        resolve(null);
      };
    });
  } else {
    y += 20;
  }

  // render invoice name
  doc.setFontSize(xLargeFontSize);
  doc.text(labels.invoiceName, maxWidth - 10, 25, { align: "right" });
  doc.setFontSize(largeFontSize);
  doc.text("# " + invoiceData.invoiceId, maxWidth - 10, 40, { align: "right" });

  // render bill from
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "bold");
  doc.text(invoiceData.issuer || "", minWidth + 10, (y += 20));

  // render bill to
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(gray);
  doc.text(labels.billTo + ":", minWidth + 10, y + 20);
  doc.setFont(font, "bold");
  doc.setTextColor(black);
  doc.text(account.firstName + " " + account.lastName, minWidth + 10, y + 35);

  // render ship to
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(gray);
  doc.text(labels.shipTo + ":", minWidth + 10, y + 55);
  doc.setFont(font, "bold");
  doc.setTextColor(black);
  doc.text(invoiceData.shippingAddress || "", minWidth + 10, y + 70);

  // render detail labels
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(gray);
  doc.text(labels.date + ":", maxWidth - 110, (y += 20), { align: "right" });
  doc.text(labels.paymentTerms + ":", maxWidth - 110, (y += 20), {
    align: "right",
  });
  doc.text(labels.dueDate + ":", maxWidth - 110, (y += 20), {
    align: "right",
  });
  doc.text(labels.poNumber + ":", maxWidth - 110, (y += 20), {
    align: "right",
  });

  // render rounded gray rectangle
  doc.setFillColor(bgGray);
  doc.roundedRect(220, y + 10, maxWidth - 220, 20, 2, 2, "F");

  // render amount due
  doc.setFont(font, "bold");
  doc.setTextColor(black);
  doc.text(labels.amountDue + ":", maxWidth - 110, (y += 23), {
    align: "right",
  });

  y -= 103;

  // render detail values
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(black);
  doc.text(dayjs.tz(date, office.timeZone).format("YYYY-MM-DD"), maxWidth - 10, (y += 20), {
    align: "right",
  });
  doc.text(paymentTerms || "", maxWidth - 10, (y += 20), { align: "right" });
  doc.text(dayjs.tz(dueDate, office.timeZone).format("YYYY-MM-DD"), maxWidth - 10, (y += 20), {
    align: "right",
  });
  doc.text(purchaseOrderNumber || "", maxWidth - 10, (y += 20), { align: "right" });

  // render amount due
  doc.setFont(font, "bold");
  doc.setTextColor(black);
  doc.text("$" + dueAmount.toFixed(2), maxWidth - 10, (y += 23), {
    align: "right",
  });

  // render pay online link
  doc.setFont(font, "normal");
  if (includePaymentLink) {
    const paymentLink = `${process.env.NEXT_PUBLIC_WEBAPP_URL}/${office.officename}/invoice?uid=${invoiceData.uid}`;

    // Set text color to blue (RGB values for a web-friendly blue)
    doc.setTextColor(0, 0, 255);

    // Set up the link text
    const linkText = "Pay Invoice";

    // Calculate text width to draw underline and set link area
    const textWidth = doc.getTextWidth(linkText);

    // Add the text
    doc.text(linkText, maxWidth - textWidth, (y += 30));

    // Draw underline
    doc.setLineWidth(0.5);
    doc.line(maxWidth - textWidth, y + 1, maxWidth - textWidth + textWidth, y + 1);

    // Add clickable link
    doc.link(maxWidth - textWidth, y - 10, textWidth, 12, { url: paymentLink });

    // Reset text color back to default
    doc.setTextColor(black);
  }

  // render items table
  // render table header
  // render rounded gray rectangle
  doc.setFillColor("#333");
  doc.roundedRect(minWidth, (y += 40), maxWidth - 10, 20, 2, 2, "F");

  // render table header
  doc.setFontSize(normalFontSize);
  doc.setTextColor(white);
  doc.setFont(font, "normal");
  doc.text(labels.productService, minWidth + 10, y + 13, { align: "left" });
  doc.text(labels.description, minWidth + 120, y + 13, { align: "left" });
  doc.text(labels.quantity, minWidth + 250, y + 13, { align: "left" });
  doc.text(labels.unitPrice, minWidth + 300, y + 13, { align: "left" });
  doc.text(labels.amount, maxWidth - 10, y + 13, { align: "right" });

  y += 10;

  // render items
  doc.setTextColor(gray);
  products.forEach((product) => {
    doc.setTextColor(black);
    doc.setFont(font, "bold");
    doc.text(product.name, minWidth + 10, y + 25, { align: "left" });
    doc.setTextColor(gray);
    doc.setFont(font, "normal");
    doc.text(product.description || "", minWidth + 120, y + 25, { align: "left" });
    doc.text(`${product.quantity}`, minWidth + 250, y + 25, { align: "left" });
    doc.text("$" + product.price.toFixed(2), minWidth + 300, y + 25, { align: "left" });
    doc.text("$" + (product.quantity * product.price).toFixed(2), maxWidth - 10, y + 25, {
      align: "right",
    });

    y += 15;

    if (y > 600) {
      doc.addPage();
      y = 10;
    }
  });

  y += 30;

  if (y > 620) {
    doc.addPage();
    y = 10;
  }
  y += 20;

  // render subtotal labels
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(gray);

  const lineHeight = 20;
  let currentY = y;

  // Create an array of labels to render
  const labelsToRender = [
    { text: labels.subtotal + ":", show: true },
    { text: labels.discount + ":", show: !!discount },
    { text: labels.tax + ":", show: !!tax },
    { text: labels.shipping + ":", show: !!shipping },
    { text: labels.total + ":", show: true },
    { text: labels.amountPaid + ":", show: !!paidAmount },
  ];

  // Render labels and track the total height
  labelsToRender.forEach(({ text, show }) => {
    if (show) {
      doc.text(text, maxWidth - 110, currentY, { align: "right" });
      currentY += lineHeight;
    }
  });

  // render subtotal values
  doc.setFontSize(normalFontSize);
  doc.setFont(font, "normal");
  doc.setTextColor(black);

  const subtotal = products.reduce((sum, product) => {
    return sum + product.price * product.quantity;
  }, 0);

  const taxAmount = taxMode == PaymentMode.PERCENTAGE ? (subtotal * tax) / 100 : tax;
  const discountAmount = discountMode == PaymentMode.PERCENTAGE ? (subtotal * discount) / 100 : discount;
  const total = subtotal + taxAmount - discountAmount + shipping;

  // Create an array of values to render
  const valuesToRender = [
    { text: "$" + subtotal.toFixed(2), show: true },
    {
      text: discountMode == PaymentMode.PERCENTAGE ? discount + "%" : "$" + discount,
      show: !!discount,
    },
    {
      text: taxMode == PaymentMode.PERCENTAGE ? tax + "%" : "$" + tax,
      show: !!tax,
    },
    {
      text: "$" + shipping,
      show: !!shipping,
    },
    { text: "$" + total.toFixed(2), show: true },
    {
      text: "$" + paidAmount.toFixed(2),
      show: !!paidAmount,
    },
  ];

  currentY = y;

  // Render values using the same line height as labels
  valuesToRender.forEach(({ text, show }) => {
    if (show) {
      doc.text(text, maxWidth - 10, currentY, { align: "right" });
      currentY += lineHeight;
    }
  });

  y = currentY;

  if (y > 620) {
    doc.addPage();
    y = 10;
  }

  // render notes
  doc.setFontSize(normalFontSize);
  doc.setTextColor(gray);
  doc.text(labels.notes + ":", minWidth + 10, (y += 20), { align: "left" });
  doc.setTextColor(black);

  // Calculate notes height
  const notesLines = doc.splitTextToSize(notes || "", 400);
  const notesHeight = doc.getTextDimensions(notesLines).h;
  doc.text(notesLines, minWidth + 10, (y += 15));

  // Add the height of notes text plus some padding
  y += notesHeight + 10;

  // render terms
  doc.setFontSize(normalFontSize);
  doc.setTextColor(gray);
  doc.text(labels.terms + ":", minWidth + 10, (y += 20), { align: "left" });
  doc.setTextColor(black);
  doc.text(terms || "", minWidth + 10, (y += 15), { align: "left", maxWidth: 400 });

  if (attachLink) {
    doc.setTextColor(0, 0, 255);
    const linkText = "Attach/Hyperlink";
    const textWidth = doc.getTextWidth(linkText);
    doc.text(linkText, 20, (y += 30));
    doc.setLineWidth(0.5);
    doc.line(20, y + 1, 20 + textWidth, y + 1);
    doc.link(20, y - 10, textWidth, 12, { url: attachLink });
    doc.setTextColor(black);
  }

  return doc;
}
